import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { QrCode, Loader2, CheckCircle2, Info, ArrowRight, ShieldCheck } from "lucide-react";
import api, { assetImage, formatIDR, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { PaymentModal } from "@/components/PaymentModal";
import { toast } from "sonner";

export default function Checkout() {
  const { t, localizedTitle } = useI18n();
  const { items, subtotal, clear, removeItem } = useCart();
  const { user, openAuth } = useAuth();
  const navigate = useNavigate();

  const [promo, setPromo] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [payOrder, setPayOrder] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const promoRates = { LUMINA10: 0.1, LUMINA20: 0.2, WELCOME: 0.15 };
  const discount = appliedPromo ? Math.round(subtotal * promoRates[appliedPromo]) : 0;
  const total = subtotal - discount;

  const applyPromo = () => {
    const code = promo.toUpperCase().trim();
    if (promoRates[code]) {
      setAppliedPromo(code);
      toast.success(`${code} applied (-${promoRates[code] * 100}%)`);
    } else {
      toast.error("Invalid promo code");
    }
  };

  const pay = async () => {
    if (user === null) return; // auth still resolving
    if (user === false) {
      toast.error(t("checkout.loginRequired"));
      openAuth("login");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/orders", {
        items: items.map((i) => ({ asset_id: i.id, license: i.license })),
        promo_code: appliedPromo,
        payment_method: "qris",
      });
      const order = data.order;
      if (!order.gateway_ok || (!order.qr_code && !order.qr_data)) {
        toast.error(t("checkout.gatewayError"));
        setLoading(false);
        return;
      }
      setPayOrder(order);
      setModalOpen(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handlePaid = () => {
    clear();
    setModalOpen(false);
    toast.success(t("checkout.success"));
    setTimeout(() => navigate("/library"), 800);
  };

  if (items.length === 0) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-2xl flex-col items-center justify-center px-6 text-center">
        <CheckCircle2 size={48} className="text-slate-200" />
        <p className="mt-4 text-lg font-semibold text-slate-900">{t("cart.empty")}</p>
        <button
          onClick={() => navigate("/browse")}
          className="mt-5 flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white"
          data-testid="checkout-browse-btn"
        >
          {t("hero.browseAll")} <ArrowRight size={16} />
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-6 py-10">
      <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">
        {t("checkout.title")}
      </h1>

      <div className="mt-8 grid gap-8 lg:grid-cols-5">
        {/* Items + payment method */}
        <div className="space-y-6 lg:col-span-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <h2 className="text-sm font-semibold text-slate-900">{t("checkout.summary")}</h2>
            <div className="mt-4 space-y-4">
              {items.map((item) => (
                <div key={item.id} className="flex items-center gap-3" data-testid={`checkout-item-${item.id}`}>
                  <img src={assetImage(item)} alt="" className="h-14 w-14 rounded-lg object-cover" />
                  <div className="min-w-0 flex-1">
                    <h4 className="line-clamp-1 text-sm font-semibold text-slate-900">{localizedTitle(item)}</h4>
                    <p className="text-xs capitalize text-slate-500">{t(`detail.${item.license}`)}</p>
                  </div>
                  <span className="font-mono text-sm font-bold text-slate-900">{formatIDR(item.price)}</span>
                  <button
                    onClick={() => removeItem(item.id)}
                    className="text-xs text-slate-400 hover:text-red-500"
                    data-testid={`checkout-remove-${item.id}`}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <h2 className="text-sm font-semibold text-slate-900">{t("checkout.method")}</h2>
            <div className="mt-4 flex items-center gap-3 rounded-xl border-2 border-blue-600 bg-blue-50 p-4" data-testid="payment-method-qris">
              <QrCode size={24} className="text-blue-700" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-blue-800">{t("checkout.qris")}</p>
                <p className="text-xs text-blue-600">GoPay · OVO · DANA · ShopeePay · Bank Apps</p>
              </div>
              <ShieldCheck size={18} className="text-emerald-500" />
            </div>
            <div className="mt-4 flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-xs text-amber-800">
              <Info size={14} className="mt-0.5 shrink-0" />
              {t("checkout.mockNote")}
            </div>
          </div>
        </div>

        {/* Summary sidebar */}
        <div className="lg:col-span-2">
          <div className="sticky top-24 rounded-2xl border border-slate-200 bg-white p-6">
            <div className="flex gap-2">
              <input
                value={promo}
                onChange={(e) => setPromo(e.target.value)}
                placeholder={t("cart.promo")}
                className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500"
                data-testid="promo-input"
              />
              <button
                onClick={applyPromo}
                className="rounded-lg bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200 transition"
                data-testid="promo-apply-btn"
              >
                {t("cart.apply")}
              </button>
            </div>

            <div className="mt-5 space-y-3 border-t border-slate-100 pt-5 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">{t("cart.subtotal")}</span>
                <span className="font-mono font-medium text-slate-900">{formatIDR(subtotal)}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-emerald-600">
                  <span>{t("cart.discount")} ({appliedPromo})</span>
                  <span className="font-mono font-medium">-{formatIDR(discount)}</span>
                </div>
              )}
              <div className="flex justify-between border-t border-slate-100 pt-3 text-base">
                <span className="font-semibold text-slate-900">{t("cart.total")}</span>
                <span className="font-mono font-extrabold text-slate-900" data-testid="checkout-total">
                  {formatIDR(total)}
                </span>
              </div>
            </div>

            <motion.button
              whileTap={{ scale: 0.98 }}
              onClick={pay}
              disabled={loading || user === null}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 py-3.5 text-sm font-semibold text-white hover:bg-blue-700 transition disabled:opacity-60"
              data-testid="checkout-pay-btn"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : null}
              {loading ? t("checkout.processing") : `${t("checkout.pay")} ${formatIDR(total)}`}
            </motion.button>
          </div>
        </div>
      </div>

      <PaymentModal order={payOrder} open={modalOpen} onClose={() => setModalOpen(false)} onPaid={handlePaid} />
    </div>
  );
}
